import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CenterFrameLayout } from 'Layouts';
import {
  addRedirectUrl,
  getUserSheerInfo,
  setSheerProgram,
  setSheerVerificationId,
} from 'State/Integrations/IntegrationsActions';
import { SHEER_PAGE_TYPES, SHEER_PAGE_STATUS } from 'Utils/SheerIdHelpers';
import { useSubscription } from 'Hooks/useSubscription';
import { SheerIdFormPage } from './SheerIdFormPage/SheerIdFormPage';
import { SheerIdUploadPage } from './SheerIdUpload/SheerIdUploadPage';
import { SheerIdReviewPage } from './SheerIdReviewPage/SheerIdReviewPage';
import { SheerIdStatusPage } from './SheerIdStatusPage/SheerIdStatusPage';
import './SheerIdVerification.css';

const components = {
  [SHEER_PAGE_TYPES.FORM]: SheerIdFormPage,
  [SHEER_PAGE_TYPES.DOC_UPLOAD]: SheerIdUploadPage,
  [SHEER_PAGE_TYPES.REVIEW]: SheerIdReviewPage,
  [SHEER_PAGE_TYPES.SUCCESS]: SheerIdStatusPage,
  [SHEER_PAGE_TYPES.ERROR]: SheerIdStatusPage,
  [SHEER_PAGE_TYPES.INELIGIBLE]: SheerIdStatusPage,
};

export const SheerIdVerificationPage = () => {
  const { bango, sheerId } = useSelector((s) => s.integrations);
  const { step } = sheerId;
  const [pageProps, setPageProps] = useState({
    pageType: step || SHEER_PAGE_TYPES.FORM,
    /* the params are either the response/error from getUserSheerInfo or an error from elsewhere
      if response, will look like:
      { verificationId, currentStep, program, partner, bangoRedirectUrl: optional }
    */
    params: {},
  });

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const location = useLocation();
  const { hasActiveSubscription, purchaseType } = useSubscription();
  const pageType = pageProps?.pageType;
  const stepParams = pageProps?.params;
  const { redirectUrl } = bango;
  const dispatch = useDispatch();
  const hasBackBangoButton =
    redirectUrl && (pageType === SHEER_PAGE_TYPES.FORM || pageType === SHEER_PAGE_TYPES.REVIEW);
  const hasBackToVerification = pageType === SHEER_PAGE_TYPES.DOC_UPLOAD || pageType === SHEER_PAGE_TYPES.ERROR;

  const setPage = useCallback(
    (newPageType, newStepParams) => {
      // Defaults when memberVerify is 404ing before form submission
      setPageProps({
        pageType: newPageType || SHEER_PAGE_TYPES.FORM,
        params: newStepParams || {},
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setPageProps]
  );

  useEffect(() => {
    const getVerificationStep = async () => {
      // Returns step, and other data associated with a user with a verification Id,
      setLoading(true);
      try {
        const verificationStep = await dispatch(getUserSheerInfo());
        if (verificationStep?.bangoRedirectUrl) dispatch(addRedirectUrl(verificationStep.bangoRedirectUrl));
        setPage(SHEER_PAGE_STATUS[verificationStep?.currentStep], verificationStep);
        setLoading(false);
      } catch (error) {
        if (error?.currentStep) setPage(SHEER_PAGE_STATUS[error?.currentStep], error);
        setLoading(false);
      }
    };

    getVerificationStep();
  }, [setPage, dispatch]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sheerProgramType = urlParams.get('sheerVerification') || pageProps.params.segment;
    if (sheerProgramType) dispatch(setSheerProgram(sheerProgramType));
    if (pageProps.params.verificationId) dispatch(setSheerVerificationId(pageProps.params.verificationId));
  }, [pageProps.params, location.search, dispatch]);

  useEffect(() => {
    //Existing Bango Users are ineligible
    if (hasActiveSubscription && purchaseType === 'bango') {
      setPage(SHEER_PAGE_TYPES.INELIGIBLE, stepParams);
    }
  }, [hasActiveSubscription, purchaseType, setPage, stepParams]);

  const layoutProps = {
    title,
    showLoader: loading,
    modernLayout: true,
    panelStyle: hasBackBangoButton || hasBackToVerification ? { marginTop: 20 } : {},
    customFrameClass: 'sheerIdFrameContainer',
  };

  const Component = components[pageType];
  return (
    <CenterFrameLayout {...layoutProps}>
      <Component setPage={setPage} pageType={pageType} setTitle={setTitle} {...stepParams} />
    </CenterFrameLayout>
  );
};
